import React, { useState } from 'react';
import { Pencil, Trash2, ExternalLink, Info } from 'lucide-react';
import { SuggestedDosage } from '../../types/data';
import { GenericTable } from '../GenericTable/GenericTable';
import { Modal } from '../Modal';

export const SuggestedDosageList: React.FC<{
  dosages: SuggestedDosage[];
  onEdit: (dosage: SuggestedDosage) => void;
  onDelete: (id: string) => void;
}> = ({ dosages, onEdit, onDelete }) => {
  const [modalContent, setModalContent] = useState<string | null>(null);

  const openModal = (content: string) => {
    setModalContent(content);
  };

  const closeModal = () => {
    setModalContent(null);
  };

  return (
    <div>
      <GenericTable
        data={dosages}
        columns={[
          { key: 'name', label: 'Name', sticky: 'left' },
          {
            key: 'amount',
            label: 'Amount',
            render: (item) =>
              `${item.amount ?? 'N/A'} ${item.amount_units ?? ''}`.trim(),
          },
          {
            key: 'reconstitution_amount',
            label: 'Reconstitution',
            render: (item) =>
              `${item.reconstitution_amount ?? 'N/A'} ${
                item.reconstitution_units ?? ''
              }`.trim(),
          },
          {
            key: 'syringe_units_per_dose',
            label: 'Syringe Units',
            render: (item) => item.syringe_units_per_dose ?? 'N/A',
          },
          {
            key: 'dosage',
            label: 'Dosage',
            render: (item) =>
              `${item.dosage ?? 'N/A'} ${item.dosage_units ?? ''}`.trim(),
          },
          {
            key: 'timing',
            label: 'Timing',
            render: (item) => item.timing ?? 'N/A',
          },
          {
            key: 'frequency_interval',
            label: 'Frequency',
            render: (item) => item.frequency_interval ?? 'N/A',
          },
          {
            key: 'duration_period',
            label: 'Duration',
            render: (item) =>
              item.duration_repeat
                ? `${item.duration_period ?? 'N/A'} / ${item.duration_repeat}`
                : item.duration_period ?? 'N/A',
          },
          {
            key: 'source',
            label: 'Source',
            render: (item) =>
              item.source ? (
                <a
                  href={item.source}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center text-blue-600 hover:text-blue-800 transition-colors"
                >
                  <ExternalLink />
                </a>
              ) : (
                'N/A'
              ),
          },
          {
            key: 'notes',
            label: 'Notes',
            render: (item) =>
              item.notes ? (
                <button
                  onClick={() => openModal(item.notes)}
                  className="text-blue-600 hover:text-blue-800 transition-colors"
                  title="View notes"
                >
                  <Info className="w-5 h-5" />
                </button>
              ) : (
                'N/A'
              ),
          },
          {
            key: 'actions',
            label: 'Actions',
            sticky: 'right',
            render: (item) => (
              <div className="flex gap-3">
                <button
                  onClick={() => onEdit(item)}
                  className="text-blue-600 hover:text-blue-800 transition-colors"
                  title="Edit"
                >
                  <Pencil className="w-5 h-5" />
                </button>
                <button
                  onClick={() => onDelete(item.id ?? '')}
                  className="text-red-600 hover:text-red-800 transition-colors"
                  title="Delete"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            ),
          },
        ]}
      />

      <Modal isOpen={!!modalContent} onClose={closeModal} title="Notes">
        <p className="text-gray-700">{modalContent}</p>
      </Modal>
    </div>
  );
};
